import clsx, { type ClassValue } from 'clsx';
import { format, isToday, isTomorrow, isYesterday, differenceInCalendarDays } from 'date-fns';

/**
 * Conditional class name joiner.
 * Thin wrapper over clsx to avoid re-importing everywhere.
 */
export function cn(...inputs: ClassValue[]): string {
    return clsx(inputs);
}

/**
 * Format a date for display in the board.
 * Today / Tomorrow / Yesterday for near-term, MMM d for dates within ~14 days,
 * MMM d yyyy for anything else.
 */
export function formatScheduleDate(iso: string | null | undefined): string {
    if (!iso) return '—';
    const d = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(d.getTime())) return '—';
    if (isToday(d)) return 'Today';
    if (isTomorrow(d)) return 'Tomorrow';
    if (isYesterday(d)) return 'Yesterday';
    const daysAway = Math.abs(differenceInCalendarDays(d, new Date()));
    if (daysAway <= 14) return format(d, 'EEE MMM d');
    return format(d, 'MMM d, yyyy');
}

/**
 * US short date: "08/28/26".
 *
 * Kristine, 28 Aug, for both date columns on the service board. Kept separate
 * from formatDateCompact, which renders "Apr 24" and is what the schedule board
 * already uses. Slices first so a timestamptz passed in by mistake still lands
 * on the right calendar day instead of shifting across the timezone.
 */
export function formatDateShort(iso: string | null | undefined): string {
    if (!iso) return '';
    const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
    if (Number.isNaN(d.getTime())) return '';
    return format(d, 'MM/dd/yy');
}

/** Short compact format: "Apr 24" */
export function formatDateCompact(iso: string | null | undefined): string {
    if (!iso) return '';
    const d = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(d.getTime())) return '';
    return format(d, 'MMM d');
}

/** ISO yyyy-MM-dd format for HTML date inputs */
export function toInputDate(iso: string | null | undefined): string {
    if (!iso) return '';
    return iso.slice(0, 10);
}

/** Today in yyyy-MM-dd */
export function todayISO(): string {
    return format(new Date(), 'yyyy-MM-dd');
}

/** Format a timestamptz for the activity log: "Apr 22, 3:45 PM" */
export function formatTimestamp(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return format(d, 'MMM d, h:mm a');
}
